import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CustomModal from '../../../Modal';
import CustomTooltip from '../../../Tooltip';
import PaymentPageSettingModalComponent from '../PaymentPageSettingModal/PaymentPageSettingModal.container';
import PaymentMethodActivationComponent from '../PaymentMethodActivation/PaymentMethodActivation.container';
import { I18nFormatter } from '../../../../utils/I18n/I18n';
import { escape } from '../../../../helpers/escape';
import UserStore from '../../../../stores/UserStore';
import DataComponentPaymentAccounts from '../../../DataComponent/DataComponentPaymentAccounts';
import FormGroup from '../../../FormGroup';
import { useI18n } from 'utils/I18n/context';

const columnTableData = {
    header: { 7: { style: { width: 100 } } },
    body: {},
};

const images = {
    edit: require('img/icons/icon-edit-blue.svg'),
    active: require('img/icons/icon-active-payment.svg'),
    activate: require('img/icons/icon-activate-payment.svg'),
    delete: require('img/icons/icon-delete-red.svg'),
};

export default function PaymentPageContainer(props) {
    const {
        headerNames,
        totalPages,
        loading,
        paymentDetails,
        paymentMethods,
        paymentSystems,
        allowPaymentSystems,
        countries,
        loadingDelete: loadingDeleteProp,
        deleteModalIsVisible: deleteModalIsVisibleProp,
        deleteAccountTitle: deleteAccountTitleProp,
        settingModalIsVisible: settingModalIsVisibleProp,
        idActivatingMethod: idActivatingMethodProp,
        getPaymentDetails,
        deletePaymentAccount,
        editPaymentAccount,
    } = props;

    const { t } = useI18n();
    const tPayment = t?.paymentPage || {};
    const tActions = t?.actions || {};

    const [deleteModalIsVisible, setDeleteModalIsVisible] = useState(!!deleteModalIsVisibleProp);
    const [deleteAccountTitle, setDeleteAccountTitle] = useState(deleteAccountTitleProp || '');
    const [deleteAccountId, setDeleteAccountId] = useState(null);
    const [loadingDelete, setLoadingDelete] = useState(!!loadingDeleteProp);

    const [settingModalIsVisible, setSettingModalIsVisible] = useState(!!settingModalIsVisibleProp);
    const [isSettingCreate, setIsSettingCreate] = useState(false);
    const [currentPaymentId, setCurrentPaymentId] = useState(undefined);

    const [idActivatingMethod, setIdActivatingMethod] = useState(idActivatingMethodProp || null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('addAccount') != null && (allowPaymentSystems || []).length) {
            setIsSettingCreate(true);
            setSettingModalIsVisible(true);
            const q = (params.delete('addAccount'), params.toString());
            window.history.replaceState({}, '', q ? `${location.pathname}?${q}` : location.pathname);
        }
    }, [allowPaymentSystems]);

    const onDataTableAction = useCallback(() => {
        getPaymentDetails && getPaymentDetails();
    }, [getPaymentDetails]);

    const closeDeleteModal = useCallback(() => {
        setDeleteModalIsVisible(false);
    }, []);

    const showDeleteModal = useCallback((id, title) => {
        setDeleteAccountId(id);
        setDeleteAccountTitle(title || '');
        setDeleteModalIsVisible(true);
    }, []);

    const showSettingModal = useCallback((isCreate, id) => {
        setIsSettingCreate(!!isCreate);
        setCurrentPaymentId(id);
        setSettingModalIsVisible(true);
    }, []);

    const deleteAccount = useCallback(() => {
        if (!deleteAccountId) return;
        setLoadingDelete(true);
        deletePaymentAccount &&
        deletePaymentAccount(deleteAccountId, () => {
            setLoadingDelete(false);
            setDeleteModalIsVisible(false);
        });
    }, [deletePaymentAccount, deleteAccountId]);

    const onActivateClick = useCallback((id) => setIdActivatingMethod(id), []);
    const onCancelActivateClick = useCallback(() => setIdActivatingMethod(null), []);
    const onDeactivateClick = useCallback(() => {
        editPaymentAccount && editPaymentAccount();
    }, [editPaymentAccount]);

    const getStatusNode = useCallback(
        (active) => <span>{active ? tPayment.active : tPayment.notActive}</span>,
        [tPayment.active, tPayment.notActive]
    );

    const detailsNames = useMemo(() => UserStore.getTranslation().paymentPage.details, []);

    const rows = useMemo(() => {
        if (!paymentMethods || !paymentMethods.length) return [];

        const list = [];
        (paymentDetails || []).forEach((payment, i) => {
            const dict = payment.payment_dictionary != null ? payment.payment_dictionary : null;

            const accountInfo = [];
            let accountInfoMobile =
                '<div className="wrap__mobile"><div className="row-title">' +
                tActions.accountInfo +
                '</div><div className="accountInfo__container row-value">';

            if (payment.full_name) {
                accountInfo.push('<strong>' + detailsNames.fullName + ':</strong> ' + payment.full_name);
                accountInfoMobile += '<div>' + detailsNames.fullName + ': ' + payment.full_name + '</div>';
            }
            if (payment.edrpou_ipn) {
                accountInfo.push('<strong>' + detailsNames.edrpouIpn + ':</strong> ' + payment.edrpou_ipn);
                accountInfoMobile += '<div>' + detailsNames.edrpouIpn + ': ' + payment.edrpou_ipn + '</div>';
            }
            if (payment.bank_name) {
                accountInfo.push('<strong>' + detailsNames.bankName + ':</strong> ' + payment.bank_name);
                accountInfoMobile += '<div>' + detailsNames.bankName + ': ' + payment.bank_name + '</div>';
            }
            if (payment.bank_account) {
                accountInfo.push('<strong>' + detailsNames.bankAccount + ':</strong> ' + payment.bank_account);
                accountInfoMobile += '<div>' + detailsNames.bankAccount + ': ' + payment.bank_account + '</div>';
            }
            if (payment.bic) {
                accountInfo.push('<strong>' + detailsNames.bic + ':</strong> ' + payment.bic);
                accountInfoMobile += '<div>' + detailsNames.bic + ': ' + payment.bic + '</div>';
            }
            if (payment.purpose_of_payment) {
                accountInfo.push('<strong>' + detailsNames.purposeOfPayment + ':</strong> ' + payment.purpose_of_payment);
                accountInfoMobile += '<div>' + detailsNames.purposeOfPayment + ': ' + payment.purpose_of_payment + '</div>';
            }
            if (payment.iban) {
                accountInfo.push('<strong>' + detailsNames.iban + ':</strong> ' + payment.iban);
                accountInfoMobile += '<div>' + detailsNames.iban + ': ' + payment.iban + '</div>';
            }
            if (payment.crypto_currency) {
                accountInfo.push('<strong>' + detailsNames.cryptoCurrency + ':</strong> ' + payment.crypto_currency);
                accountInfoMobile += '<div>' + detailsNames.cryptoCurrency + ': ' + payment.crypto_currency + '</div>';
            }
            if (payment.crypto_network) {
                accountInfo.push('<strong>' + detailsNames.cryptoNetwork + ':</strong> ' + payment.crypto_network);
                accountInfoMobile += '<div>' + detailsNames.cryptoNetwork + ': ' + payment.crypto_network + '</div>';
            }
            if (payment.card_number) {
                accountInfo.push('<strong>' + detailsNames.cardNumber + ':</strong> ' + payment.card_number);
                accountInfoMobile += '<div>' + detailsNames.cardNumber + ': ' + payment.card_number + '</div>';
            }
            if (payment.description) {
                accountInfo.push('<strong>' + detailsNames.description + ':</strong> ' + payment.description);
                accountInfoMobile += '<div>' + detailsNames.description + ': ' + payment.description + '</div>';
            }
            accountInfoMobile += '</div></div>';

            const rowClassName = payment.active ? 'green-bg' : null;
            const statusClassName = payment.active ? 'row-value green-bg' : 'row-value';

            list.push({
                className: rowClassName,
                title: (
                    <div>
                        <span className="wrap__desktop">{escape(payment.title)}</span>
                        <div className="wrap__mobile">
                            <div className="row-title">{tActions.accountName}</div>
                            <div className="row-value">{escape(payment.title)}</div>
                        </div>
                    </div>
                ),
                status: (
                    <div>
                        <span className="wrap__desktop">{getStatusNode(payment.active)}</span>
                        <div className="wrap__mobile">
                            <div className="row-title">{tActions.paymentStatus}</div>
                            <div className={statusClassName}>{getStatusNode(payment.active)}</div>
                        </div>
                    </div>
                ),
                type: (
                    <div>
                        <span className="wrap__desktop">{dict ? dict.title : null}</span>
                        <div className="wrap__mobile">
                            <div className="row-title">{tActions.paymentDictionary}</div>
                            <div className="row-value">{dict ? dict.title : null}</div>
                        </div>
                    </div>
                ),
                currency: (
                    <div>
                        <span className="wrap__desktop">{payment.currency}</span>
                        <div className="wrap__mobile">
                            <div className="row-title">{tActions.currency}</div>
                            <div className="row-value">{payment.currency}</div>
                        </div>
                    </div>
                ),
                accountInfo: '<div className="wrap__desktop">' + accountInfo.join('<br>') + '</div>',
                accountInfoMobile,
                createdAt: (
                    <div>
                        <span className="wrap__desktop">{I18nFormatter.fullDateTime(+payment.created_at)}</span>
                        <div className="wrap__mobile">
                            <div className="row-title">{tActions.createdAt}</div>
                            <div className="row-value">{I18nFormatter.fullDateTime(+payment.created_at)}</div>
                        </div>
                    </div>
                ),
                updatedAt: (
                    <div>
                        <span className="wrap__desktop">{I18nFormatter.fullDateTime(+payment.updated_at)}</span>
                        <div className="wrap__mobile">
                            <div className="row-title">{tActions.updatedAt}</div>
                            <div className="row-value">{I18nFormatter.fullDateTime(+payment.updated_at)}</div>
                        </div>
                    </div>
                ),
                actions: (
                    <div className="row-controls text-center">
                        {!payment.active ? (
                            <>
                                <CustomTooltip id={`controlActive-${i}`} tooltip={tActions.activate}>
                                    <a className="control-item activate-button" href="#!">
                                        <span onClick={() => onActivateClick(payment.id)}>
                                            <img className="wrap__desktop" src={images.activate} alt="" />
                                            <b className="wrap__mobile">{tActions.activate}</b>
                                        </span>
                                    </a>
                                </CustomTooltip>

                                <CustomTooltip id={`controlEdit-${i}`} tooltip={tActions.edit}>
                                    <a className="control-item edit-button" href="#!">
                                        <span onClick={() => showSettingModal(false, payment.id)}>
                                            <img className="wrap__desktop" src={images.edit} alt="" />
                                            <b className="wrap__mobile">{tActions.edit}</b>
                                        </span>
                                    </a>
                                </CustomTooltip>

                                <CustomTooltip id={`controlDelete-${i}`} tooltip={tActions.delete}>
                                    <a className="control-item delete-button" href="#!">
                                        <span onClick={() => showDeleteModal(payment.id, payment.title)}>
                                            <img className="wrap__desktop" src={images.delete} alt="" />
                                            <b className="wrap__mobile">{tActions.delete}</b>
                                        </span>
                                    </a>
                                </CustomTooltip>
                            </>
                        ) : (
                            <CustomTooltip id={`controlDeactivate-${i}`} tooltip={tActions.deactivate}>
                                <a className="control-item deactivate-button" href="#!">
                                    <span onClick={onDeactivateClick}>
                                        <img className="wrap__desktop" src={images.active} alt="" />
                                        <b className="wrap__mobile">{tActions.deactivate}</b>
                                    </span>
                                </a>
                            </CustomTooltip>
                        )}
                    </div>
                ),
            });
        });

        return list;
    }, [paymentMethods, paymentDetails, tActions, detailsNames, getStatusNode, onActivateClick, showSettingModal, showDeleteModal, onDeactivateClick]);

    return (
        <div className="form form-payment-accounts">
            <div className="form-body data-table accounts-table">
                <CustomModal
                    visible={deleteModalIsVisible}
                    onClose={closeDeleteModal}
                    onConfirm={deleteAccount}
                    bsSize="sm"
                    loading={loadingDelete}
                >
                    <p>
                        {tPayment.deleteNoty} {'«' + deleteAccountTitle + '»'}?
                    </p>
                </CustomModal>

                <DataComponentPaymentAccounts
                    onAction={onDataTableAction}
                    totalPages={totalPages}
                    headerNames={headerNames}
                    data={rows}
                    loading={loading}
                    columnData={columnTableData}
                    className="simple-table"
                />

                <PaymentPageSettingModalComponent
                    visible={settingModalIsVisible}
                    isCreate={isSettingCreate}
                    onClose={() => setSettingModalIsVisible(false)}
                    onSubmit={() => setSettingModalIsVisible(false)}
                    payment={{
                        currentPaymentId,
                        paymentMethods,
                        paymentDetails,
                        paymentSystems,
                        allowPaymentSystems,
                        countries,
                    }}
                />

                {idActivatingMethod ? (
                    <PaymentMethodActivationComponent id={idActivatingMethod} onCancel={onCancelActivateClick} />
                ) : null}
            </div>

            <div className="form-footer">
                <div className="form-group">
                    <FormGroup fieldClass="save-form">
                        <button type="button" onClick={() => showSettingModal(true)} className="btn apply-btn">
                            {tPayment.addAccount}
                        </button>
                    </FormGroup>
                </div>
            </div>
        </div>
    );
}

PaymentPageContainer.propTypes = {
    headerNames: PropTypes.array,
    totalPages: PropTypes.number,
    loading: PropTypes.bool,
    paymentDetails: PropTypes.array,
    paymentMethods: PropTypes.array,
    paymentSystems: PropTypes.array,
    allowPaymentSystems: PropTypes.array,
    countries: PropTypes.array,
    loadingDelete: PropTypes.bool,
    deleteModalIsVisible: PropTypes.bool,
    settingModalIsVisible: PropTypes.bool,
    deleteAccountTitle: PropTypes.string,
    idActivatingMethod: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    getPaymentDetails: PropTypes.func,
    deletePaymentAccount: PropTypes.func,
    editPaymentAccount: PropTypes.func,
};
